import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Plus, Pause, Play } from 'lucide-react'
import { format } from 'date-fns'
import {
  listScrapeJobs,
  createScrapeJob,
  pauseScrapeJob,
  resumeScrapeJob,
  listICPs,
} from '@/lib/api'
import type { ScrapeJob } from '@/schemas'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const createJobSchema = z.object({
  source_type: z.enum(['company_page', 'personal_profile']),
  source_url: z.string().url('Must be a valid URL'),
  max_followers: z.coerce.number().int().min(1).max(10000).optional(),
  icp_id: z.string().optional(),
})
type CreateJobForm = z.infer<typeof createJobSchema>

const STATUS_VARIANTS: Record<
  ScrapeJob['status'],
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  queued: 'secondary',
  running: 'default',
  paused: 'outline',
  completed: 'outline',
  failed: 'destructive',
}

export function ScrapeJobs() {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['scrape-jobs'],
    queryFn: listScrapeJobs,
    refetchInterval: 5000,
  })

  const { data: icpsData } = useQuery({
    queryKey: ['icps'],
    queryFn: listICPs,
  })

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<CreateJobForm>({
    resolver: zodResolver(createJobSchema),
    defaultValues: { source_type: 'company_page', max_followers: 500 },
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateJobForm) =>
      createScrapeJob({
        source_type: data.source_type,
        source_url: data.source_url,
        max_followers: data.max_followers,
        icp_id: data.icp_id || undefined,
      }),
    onSuccess: () => {
      toast.success('Scrape job created.')
      qc.invalidateQueries({ queryKey: ['scrape-jobs'] })
      setOpen(false)
      reset()
    },
    onError: () => toast.error('Failed to create job.'),
  })

  const pauseMutation = useMutation({
    mutationFn: (id: string) => pauseScrapeJob(id),
    onSuccess: () => {
      toast.success('Job paused.')
      qc.invalidateQueries({ queryKey: ['scrape-jobs'] })
    },
    onError: () => toast.error('Could not pause job.'),
  })

  const resumeMutation = useMutation({
    mutationFn: (id: string) => resumeScrapeJob(id),
    onSuccess: () => {
      toast.success('Job resumed.')
      qc.invalidateQueries({ queryKey: ['scrape-jobs'] })
    },
    onError: () => toast.error('Could not resume job.'),
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Scrape Jobs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor and manage LinkedIn scraping tasks.
          </p>
        </div>
        <Button size="sm" onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" />
          New job
        </Button>
      </div>

      <div className="rounded-lg border bg-card">
        {isLoading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !data?.jobs.length ? (
          <div className="py-14 text-center text-sm text-muted-foreground">
            No scrape jobs yet. Create one to start importing leads.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Source</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Type</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Scraped</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Max</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Created</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.jobs.map((job) => (
                  <tr key={job.job_id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="px-3 py-2.5 font-mono text-xs truncate max-w-[220px]">
                      {job.source_url}
                    </td>
                    <td className="px-3 py-2.5 text-xs capitalize text-muted-foreground">
                      {job.source_type.replace('_', ' ')}
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge variant={STATUS_VARIANTS[job.status]} className="capitalize text-xs">
                        {job.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5 text-sm">{job.followers_scraped}</td>
                    <td className="px-3 py-2.5 text-sm text-muted-foreground">
                      {job.max_followers ?? '—'}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">
                      {format(new Date(job.created_at), 'd MMM yyyy HH:mm')}
                    </td>
                    <td className="px-3 py-2.5">
                      {job.status === 'running' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => pauseMutation.mutate(job.job_id)}
                          disabled={pauseMutation.isPending}
                        >
                          <Pause className="h-3.5 w-3.5" />
                          Pause
                        </Button>
                      )}
                      {job.status === 'paused' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => resumeMutation.mutate(job.job_id)}
                          disabled={resumeMutation.isPending}
                        >
                          <Play className="h-3.5 w-3.5" />
                          Resume
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New scrape job</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={handleSubmit((d) => createMutation.mutate(d))}
            className="space-y-4 pt-2"
          >
            <div className="space-y-1.5">
              <Label>Source type</Label>
              <Select
                value={watch('source_type')}
                onValueChange={(v) =>
                  setValue('source_type', v as 'company_page' | 'personal_profile')
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company_page">Company page followers</SelectItem>
                  <SelectItem value="personal_profile">Personal profile connections</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>LinkedIn URL</Label>
              <Input
                placeholder="https://linkedin.com/company/example"
                {...register('source_url')}
              />
              {errors.source_url && (
                <p className="text-xs text-destructive">{errors.source_url.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Max followers</Label>
              <Input
                type="number"
                min={1}
                placeholder="500"
                {...register('max_followers')}
              />
              {errors.max_followers && (
                <p className="text-xs text-destructive">{errors.max_followers.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>ICP (optional)</Label>
              <Select
                value={watch('icp_id') ?? '__none__'}
                onValueChange={(v) =>
                  setValue('icp_id', v === '__none__' ? undefined : v)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="No ICP filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No ICP filter</SelectItem>
                  {icpsData?.icps.map((icp) => (
                    <SelectItem key={icp.icp_id} value={icp.icp_id}>
                      {icp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating…' : 'Create job'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
